/**
 * skipNotice — user-facing copy for a non-proposal `project_chat_message_results` row.
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────────────────────────────────
 * The locked-artifact pre-flight (`project-chat.service.ts`) writes a result row per TAGGED file so
 * that "skipped" is distinguishable from "never tagged", and it writes NO agent row when every
 * tagged file is locked. The UI consequence is the bug this module fixes: the user sends an
 * instruction, no agent turn ever appears, and the only trace is a small grey line reading
 * "Project Plan — Skipped". That reads as the app doing nothing, not as the app refusing.
 *
 * So each skip is turned into three separate pieces of copy:
 *   • `title`       what happened, naming the file;
 *   • `explanation` why, in product language;
 *   • `nextStep`    what the user can actually DO next, naming a workflow that exists today.
 *
 * ── WHY `nextStep` NAMES ONLY EXISTING WORKFLOWS ───────────────────────────────────────────────
 * ✅ Finding F-1 is RESOLVED: editing a committed artifact starts a NEW VERSION, and the committed
 * version stays in the project's history (2026-10-03 P0 fix-batch design §5.3, AD-9). This module
 * previously said the decision was open and that it "must not imply the decision either way".
 * That is no longer true and the copy now states the resolution plainly. Every next step below
 * still points at a control that actually ships:
 *   • `pending_commit` → Withdraw, on `CommitRequestPanel` (DELETE …/commit-request).
 *   • `committed`      → "Start new version", the banner control on `MarkdownLiveEditor` (Item 4
 *                        §5.4a, which replaced the unexplained "Edit Again" button). Direct editing
 *                        of a committed artifact has always been allowed — `patch-artifact.ts`
 *                        rejects only `pending_commit` — so this is the real path, not a workaround.
 *
 * ── WHY THE `committed` BRANCH STILL EXISTS AT ALL ─────────────────────────────────────────────
 * 🚨 Do NOT delete it to "finish Item 4". §5.4d defers its removal to §10.3 **step 5**, after the
 * step-4 config flip has held. Until that flip, `committed` is still in
 * `DEFAULT_LOCKED_ARTIFACT_STATUSES` (see the long comment there and AD-9b), so chat really does
 * still skip committed files and this really is the copy that window shows. `classifyLockedSkip`'s
 * `"this file is committed"` match is retained for the same reason, and additionally so an
 * in-flight pre-Item-4 result row still renders sensibly during the deploy window.
 *
 * ── WHY THE SERVER REASON IS CLASSIFIED RATHER THAN PRINTED ────────────────────────────────────
 * `SKIP_REASON_BY_STATUS` is good operator copy but not good user copy, so the reason string is
 * matched to a lock kind and replaced with product copy. The raw reason is still returned as
 * `serverReason` and rendered as secondary detail, so nothing the server said is lost and an
 * unrecognised reason degrades to exactly today's behaviour.
 *
 * NOTE: no branch in this module may emit copy referencing "F-1". The server's `committed` reason
 * string used to end "(see CR finding F-1)" and was rewritten in the same change — telling a user
 * that a settled decision is open is a defect, not a cosmetic issue. Asserted by test 47.
 */

import type { ArtifactType } from "@/hooks/useArtifacts";

/** Which lock the server hit, inferred from its reason text. */
export type LockKind = "pending_commit" | "approving" | "committed" | "unknown";

export interface SkipNotice {
  /** One line, names the file, says what happened. */
  title: string;
  /** Why the file was not touched. */
  explanation: string;
  /** What to do next, or null when there is genuinely nothing to suggest. */
  nextStep: string | null;
  /** True when opening the file is part of the next step, so the caller can offer a button. */
  offersOpenFile: boolean;
  /** The server's verbatim reason, for secondary display. Null when absent. */
  serverReason: string | null;
  /** 'warning' renders as an amber callout, 'neutral' as a plain row. */
  tone: "warning" | "neutral";
}

/**
 * Infer the lock kind from the server's reason text.
 *
 * Substring matching is deliberate and deliberately narrow: these three strings are owned by
 * `SKIP_REASON_BY_STATUS` in `project-chat.service.ts` and each is matched on its most distinctive
 * phrase rather than on the whole sentence, so copy-editing the server text does not silently
 * reclassify a skip. Anything unmatched is `unknown`, which falls back to printing the server's
 * own words — the same behaviour the UI had before this module existed.
 */
export function classifyLockedSkip(
  reason: string | null | undefined,
): LockKind {
  if (!reason) return "unknown";
  const text = reason.toLowerCase();
  if (text.includes("commit request is pending")) return "pending_commit";
  if (text.includes("being committed to github")) return "approving";
  if (text.includes("this file is committed")) return "committed";
  return "unknown";
}

/** Copy for every non-proposal resolution. `artifactLabel` is the human file name. */
export function describeSkip(
  artifactLabel: string,
  resolution: string,
  reason: string | null | undefined,
): SkipNotice {
  const serverReason =
    reason && reason.trim().length > 0 ? reason.trim() : null;

  if (resolution === "skipped_locked") {
    const kind = classifyLockedSkip(serverReason);

    if (kind === "pending_commit") {
      return {
        title: `${artifactLabel} was not changed — a commit request is awaiting approval.`,
        explanation:
          "Chat cannot edit a file while its commit request is open, because the approver has to review the exact content that was submitted.",
        nextStep: `Withdraw the commit request on ${artifactLabel}, then send this instruction again.`,
        offersOpenFile: true,
        serverReason,
        tone: "warning",
      };
    }

    if (kind === "approving") {
      return {
        title: `${artifactLabel} was not changed — it is being committed to GitHub right now.`,
        explanation:
          "This takes a few seconds. Editing during the commit would overwrite the version being saved.",
        nextStep: "Send the instruction again in a moment.",
        offersOpenFile: false,
        serverReason,
        tone: "warning",
      };
    }

    if (kind === "committed") {
      return {
        title: `${artifactLabel} was not changed — it is committed, and chat does not edit committed files yet.`,
        explanation:
          "A committed file is a signed-off version that was approved and saved to GitHub. Editing it starts a new version — the committed version stays in the project's history, unchanged.",
        nextStep: `Open ${artifactLabel} and choose “Start new version”, then edit that version. The new version needs its own approval before it is committed.`,
        offersOpenFile: true,
        serverReason,
        tone: "warning",
      };
    }

    return {
      title: `${artifactLabel} was not changed — its current status does not allow edits.`,
      explanation:
        serverReason ??
        "The file is locked. Open it to see its current status.",
      nextStep: `Open ${artifactLabel} to see what is blocking it.`,
      offersOpenFile: true,
      // Already shown as the explanation — do not print it twice.
      serverReason: null,
      tone: "warning",
    };
  }

  if (resolution === "skipped_forbidden") {
    return {
      title: `${artifactLabel} was not changed — you do not have permission to edit it.`,
      explanation:
        serverReason ?? "Ask a project admin for access to this file.",
      nextStep: null,
      offersOpenFile: false,
      serverReason: null,
      tone: "warning",
    };
  }

  if (resolution === "stale") {
    return {
      title: `${artifactLabel} — this proposal is out of date.`,
      explanation:
        "Applying it would discard someone else’s work: the file changed after this proposal was generated.",
      nextStep:
        "Send the instruction again to redraft against the current content.",
      offersOpenFile: false,
      serverReason,
      tone: "warning",
    };
  }

  if (resolution === "failed") {
    return {
      title: `${artifactLabel} — the edit could not be completed.`,
      explanation:
        serverReason ?? "Something went wrong while drafting this change.",
      nextStep: "Try sending the instruction again.",
      offersOpenFile: false,
      serverReason: null,
      tone: "warning",
    };
  }

  // accepted / rejected — settled, no action, no alarm.
  const settled: Record<string, string> = {
    // "accepted" vs "applied" is not a synonym here. `resolution='proposed'` means NOTHING has
    // been written to `artifact_edits.content`; acceptance is what performs the write. The
    // transcript must distinguish PROPOSED from IN THE DOCUMENT — the same reason `summarise()`
    // says "Drafted" rather than "Created" (§4.3).
    accepted: `${artifactLabel} — change applied.`,
    rejected: `${artifactLabel} — proposal rejected.`,
  };
  return {
    title: settled[resolution] ?? `${artifactLabel} — ${resolution}`,
    explanation: "",
    nextStep: null,
    offersOpenFile: false,
    serverReason,
    tone: "neutral",
  };
}

/**
 * Turn-level summary for a user message whose every tagged file was skipped.
 *
 * This is the piece that closes the "silent no-op" gap. When nothing is editable the backend
 * creates no agent row at all, so without this banner the transcript's answer to the instruction is
 * the absence of an answer. Returns null whenever an agent turn will exist (something was editable)
 * or there is nothing to summarise.
 */
export function describeAllSkippedTurn(
  skippedLabels: readonly string[],
): { title: string; detail: string } | null {
  if (skippedLabels.length === 0) return null;

  if (skippedLabels.length === 1) {
    return {
      title: "No changes were made.",
      detail: `${skippedLabels[0]} is locked, so there was nothing for the agent to edit. No agent reply was generated for this message.`,
    };
  }

  return {
    title: "No changes were made.",
    detail: `All ${skippedLabels.length} tagged files are locked (${skippedLabels.join(", ")}), so there was nothing for the agent to edit. No agent reply was generated for this message.`,
  };
}

/** Artifact types in a turn's results that were skipped by the pre-flight. */
export function lockedArtifactTypes(
  results: readonly { artifact_type: ArtifactType; resolution: string }[],
): ArtifactType[] {
  return results
    .filter(
      (r) =>
        r.resolution === "skipped_locked" ||
        r.resolution === "skipped_forbidden",
    )
    .map((r) => r.artifact_type);
}
